import { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import Login from '@/pages/Login';
import Setup from '@/pages/Setup';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import Dashboard from '@/pages/Dashboard';
import Analytics from '@/pages/Analytics';
import Requesters from '@/pages/Requesters';
import Inventory from '@/pages/Inventory';
import InternalInventory from '@/pages/InternalInventory';
import Categories from '@/pages/Categories';
import Warehouses from '@/pages/Warehouses';
import Ledger from '@/pages/Ledger';
import RackAssignment from '@/pages/RackAssignment';
import Orders from '@/pages/Orders';
import PickList from '@/pages/PickList';
import PickExecution from '@/pages/PickExecution';
import Integrations from '@/pages/Integrations';
import APISettings from '@/pages/APISettings';
import APIDocumentation from '@/pages/APIDocumentation';
import UserManagement from '@/pages/UserManagement';
import AuditLogs from '@/pages/AuditLogs';
import CycleCounts from '@/pages/CycleCounts';
import DashboardLayout from '@/components/DashboardLayout';
import ChangePasswordDialog from '@/components/ChangePasswordDialog';
import { Toaster } from '@/components/ui/sonner';
import {
  getStoredToken,
  getStoredUser,
  persistAuth,
  clearAuth,
  setUnauthorizedHandler,
  hasPermission,
} from '@/lib/auth';

function ProtectedRoute({ isAuthenticated, user, requiredPermission, children }) {
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (requiredPermission && !hasPermission(user, requiredPermission)) {
    return <Navigate to="/" replace />;
  }
  return children;
}

/**
 * Redirects the visitor to /setup on first run (no users exist yet) so the
 * super admin can create their own account with their own password.
 */
function SetupGate({ isAuthenticated }) {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (isAuthenticated) return;
    // Skip on public non-login routes — the user is intentionally there.
    if (['/setup', '/forgot-password', '/reset-password'].includes(location.pathname)) return;
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    axios.get(`${API}/auth/setup-status`).then((res) => {
      if (res.data?.needs_setup) {
        navigate('/setup', { replace: true });
      }
    }).catch(() => {});
  }, [isAuthenticated, location.pathname, navigate]);
  return null;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(
    () => !!(getStoredToken() && getStoredUser()),
  );
  const [user, setUser] = useState(() => getStoredUser());
  const [mustChangePassword, setMustChangePassword] = useState(
    () => !!(getStoredUser()?.must_change_password),
  );

  const handleLogout = useCallback(() => {
    clearAuth();
    setIsAuthenticated(false);
    setUser(null);
    setMustChangePassword(false);
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      clearAuth();
      setIsAuthenticated(false);
      setUser(null);
      setMustChangePassword(false);
    });
  }, []);

  const handleLogin = (token, u) => {
    persistAuth(token, u);
    setIsAuthenticated(true);
    setUser(u);
    setMustChangePassword(!!u.must_change_password);
  };

  const handlePasswordChanged = () => {
    // Refresh stored user (must_change_password now false)
    const updated = { ...user, must_change_password: false };
    persistAuth(getStoredToken(), updated);
    setUser(updated);
    setMustChangePassword(false);
  };

  const layout = (children) => (
    <DashboardLayout user={user} onLogout={handleLogout}>
      {children}
    </DashboardLayout>
  );

  return (
    <div className="App">
      <BrowserRouter>
        <SetupGate isAuthenticated={isAuthenticated} />
        <Routes>
          <Route
            path="/setup"
            element={
              isAuthenticated ? <Navigate to="/" replace /> : <Setup onSetupComplete={handleLogin} />
            }
          />
          <Route
            path="/login"
            element={
              isAuthenticated ? <Navigate to="/" replace /> : <Login onLogin={handleLogin} />
            }
          />
          <Route
            path="/forgot-password"
            element={
              isAuthenticated ? <Navigate to="/" replace /> : <ForgotPassword />
            }
          />
          <Route
            path="/reset-password"
            element={
              isAuthenticated ? <Navigate to="/" replace /> : <ResetPassword onLogin={handleLogin} />
            }
          />
          <Route path="/" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} user={user}>{layout(<Dashboard />)}</ProtectedRoute>
          } />
          <Route path="/analytics" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} user={user}>{layout(<Analytics />)}</ProtectedRoute>
          } />
          <Route path="/requesters" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} user={user}>{layout(<Requesters user={user} />)}</ProtectedRoute>
          } />
          <Route path="/inventory" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requiredPermission="customer_inventory.view">{layout(<Inventory />)}</ProtectedRoute>
          } />
          <Route path="/internal-inventory" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requiredPermission="internal_inventory.view">{layout(<InternalInventory />)}</ProtectedRoute>
          } />
          <Route path="/categories" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requiredPermission="categories.view">{layout(<Categories />)}</ProtectedRoute>
          } />
          <Route path="/inventory/ledger" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requiredPermission="ledger.view">
              {layout(
                <Ledger
                  inventoryTypeLock="customer"
                  title="Customer Inventory Ledger"
                  description="Inbound and outbound movements for customer products"
                />,
              )}
            </ProtectedRoute>
          } />
          <Route path="/internal-inventory/ledger" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requiredPermission="ledger.view">
              {layout(
                <Ledger
                  inventoryTypeLock="internal"
                  title="Internal Materials Ledger"
                  description="Inbound and outbound movements for internal items (spare parts, materials, tools)"
                />,
              )}
            </ProtectedRoute>
          } />
          <Route path="/rack-assignment" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requiredPermission="racks.assign">{layout(<RackAssignment />)}</ProtectedRoute>
          } />
          <Route path="/ledger" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requiredPermission="ledger.view">{layout(<Ledger />)}</ProtectedRoute>
          } />
          <Route path="/warehouses" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requiredPermission="warehouses.view">{layout(<Warehouses />)}</ProtectedRoute>
          } />
          <Route path="/orders" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requiredPermission="orders.view">{layout(<Orders />)}</ProtectedRoute>
          } />
          <Route path="/pick-list" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requiredPermission="orders.pick">{layout(<PickList />)}</ProtectedRoute>
          } />
          <Route path="/pick-execution/:orderId" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requiredPermission="orders.pick"><PickExecution /></ProtectedRoute>
          } />
          <Route path="/integrations" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requiredPermission="integrations.manage">{layout(<Integrations />)}</ProtectedRoute>
          } />
          <Route path="/api-settings" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requiredPermission="api_keys.manage">{layout(<APISettings />)}</ProtectedRoute>
          } />
          <Route path="/api-docs" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} user={user}>{layout(<APIDocumentation />)}</ProtectedRoute>
          } />
          <Route path="/users" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requiredPermission="users.manage">{layout(<UserManagement />)}</ProtectedRoute>
          } />
          <Route path="/audit-logs" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requiredPermission="audit.view">{layout(<AuditLogs />)}</ProtectedRoute>
          } />
          <Route path="/cycle-counts" element={
            <ProtectedRoute isAuthenticated={isAuthenticated} user={user} requiredPermission="cycle_count.view">{layout(<CycleCounts />)}</ProtectedRoute>
          } />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
      {isAuthenticated && (
        <ChangePasswordDialog
          open={mustChangePassword}
          forced
          onOpenChange={() => {}}
          onSuccess={handlePasswordChanged}
        />
      )}
    </div>
  );
}

export default App;
